import {
    Alert,
    Button,
    Headline,
    Loading,
    Spacer,
    StyleFunction,
    Table,
    useForm,
    useThemedStyle,
    useToast,
} from "@venuepos/react-common";
import {
    GQIntegrationQuery,
    useApproveFiskalyEstablishmentSubmissionMutation,
    useFiskalyEstablishmentsQuery,
    useFiskalyEstablishmentSubmissionsQuery,
    useGetFiskalyTaxPayerQuery,
    useIntegrationSettingsQuery,
    useRequestFiskalyEstablishmentSubmissionDownloadMutation,
    useRequestFiskalyEstablishmentSubmissionMutation,
    useRequestFiskalyEstablishmentSubmissionPreviewDownloadMutation,
    useRequestFiskalyEstablishmentSubmissionTransmissionMutation,
} from "graphql-sdk";
import { AvailableLocale, useTranslation } from "locales";
import { useCallback } from "react";
import React, { View } from "react-native";
import { Divider } from "react-native-paper";
import { useHandleMutationError } from "../../../../hooks";
import {
    defaultEstablishmentSubmissionForm,
    schemaEstablishmentSubmissionForm,
} from "../fiskaly-form";
import { SubmissionForm } from "./form";
import { b64toBlob } from "../../../export/dsfinvk";
import { FiskalySettings, formatDate, formatRelativeDate } from "lib";
import { useAdminSession } from "../../../../session";

export function FiskalySubmissionTab({
    integration,
}: {
    integration: GQIntegrationQuery["integration"];
}) {
    const [{ locale }] = useAdminSession(["locale"]);
    const [t] = useTranslation();
    const styles = useThemedStyle(styleFunc);
    const toast = useToast();
    const { data, loading, error, refetch } =
        useFiskalyEstablishmentSubmissionsQuery({
            variables: {
                pagination: {
                    limit: 10,
                    nextPageToken: "", // The initial call for estab. submissions has no token to supply. This means: get the first page.
                },
            },
        });

    const integrationSettings = useIntegrationSettingsQuery({
        variables: {
            integrationId: integration.id,
        },
    });

    const {
        data: taxPayerData,
        loading: taxPayerLoading,
        error: taxPayerError,
    } = useGetFiskalyTaxPayerQuery();

    const [requestSubmission] =
        useRequestFiskalyEstablishmentSubmissionMutation();
    const [getPreviewDownload] =
        useRequestFiskalyEstablishmentSubmissionPreviewDownloadMutation();
    const [getSubmissionDownload] =
        useRequestFiskalyEstablishmentSubmissionDownloadMutation();
    const [markSubmissionAsReviewed] =
        useApproveFiskalyEstablishmentSubmissionMutation();
    const [transmitSubmission] =
        useRequestFiskalyEstablishmentSubmissionTransmissionMutation();

    const { handleMutationError } = useHandleMutationError();
    const {
        data: establishmentsData,
        loading: establishmentsLoading,
        error: establishmentsError,
        refetch: establishmentsRefetch,
    } = useFiskalyEstablishmentsQuery({
        variables: { integrationId: integration.id },
    });

    const form = useForm(
        schemaEstablishmentSubmissionForm,
        defaultEstablishmentSubmissionForm
    );

    const [{ values }] = form;

    const save = useCallback(async () => {
        if (!values) {
            return;
        }

        await handleMutationError(
            async () => {
                await requestSubmission({
                    variables: {
                        establishmentId: values.establishmentId,
                    },
                });
            },
            t("common.created", "Created"),
            refetch
        );
    }, [handleMutationError, refetch, requestSubmission, t, values]);

    const getPreviewFile = useCallback(
        async (submissionId: string) => {
            if (!values) {
                return;
            }

            let response;
            await handleMutationError(
                async () => {
                    response = await getPreviewDownload({
                        variables: {
                            submissionId,
                        },
                    });
                },
                t("common.done", "Done"),
                () => {
                    if (
                        !response ||
                        !response?.data
                            .requestFiskalyEstablishmentSubmissionPreviewDownload
                            .fileContents
                    ) {
                        return;
                    }

                    refetch();

                    const link = document.createElement("a");
                    document.body.appendChild(link);
                    link.setAttribute("style", "display: none");

                    const blob = b64toBlob(
                        response.data
                            .requestFiskalyEstablishmentSubmissionPreviewDownload
                            .fileContents,
                        "application/pdf"
                    );

                    const url = window.URL.createObjectURL(blob);
                    link.href = url;
                    link.download = submissionId;
                    link.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(link);
                }
            );
        },
        [getPreviewDownload, handleMutationError, refetch, t, values]
    );
    const getSubmissionFile = useCallback(
        async (submissionId: string) => {
            if (!values) {
                return;
            }

            let response;
            await handleMutationError(
                async () => {
                    response = await getSubmissionDownload({
                        variables: {
                            submissionId,
                        },
                    });
                },
                t("common.done", "Done"),
                () => {
                    if (
                        !response ||
                        !response?.data
                            .requestFiskalyEstablishmentSubmissionDownload
                            .fileContents
                    ) {
                        return;
                    }

                    refetch();

                    const link = document.createElement("a");
                    document.body.appendChild(link);
                    link.setAttribute("style", "display: none");

                    const blob = b64toBlob(
                        response.data
                            .requestFiskalyEstablishmentSubmissionDownload
                            .fileContents,
                        "application/pdf"
                    );

                    const url = window.URL.createObjectURL(blob);
                    link.href = url;
                    link.download = submissionId;
                    link.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(link);
                }
            );
        },
        [getSubmissionDownload, handleMutationError, refetch, t, values]
    );

    const approveSubmission = useCallback(
        async (submissionId: string) => {
            if (!values) {
                return;
            }

            let response;
            await handleMutationError(
                async () => {
                    response = await markSubmissionAsReviewed({
                        variables: {
                            submissionId,
                        },
                    });
                },
                t(
                    "backoffice.integration.fiskaly.submission_reviewed",
                    "Submission is marked as reviewed"
                ),
                () => {
                    if (
                        !response ||
                        !response?.data.approveFiskalyEstablishmentSubmission
                            .status
                    ) {
                        return;
                    }

                    refetch();
                }
            );
        },
        [handleMutationError, markSubmissionAsReviewed, refetch, t, values]
    );

    const startTransmission = useCallback(
        async (submissionId: string) => {
            if (!values) {
                return;
            }

            await handleMutationError(
                async () => {
                    await transmitSubmission({
                        variables: {
                            submissionId,
                        },
                    });
                },
                t(
                    "backoffice.integration.fiskaly.submission_transmitted",
                    "Transmission request for Submission is done."
                ),
                () => {
                    // Refetch the list of submissions
                    // .. and the establishment list, since that's where the "submit before" dates are stored.
                    refetch();
                    establishmentsRefetch();
                }
            );
        },
        [
            establishmentsRefetch,
            handleMutationError,
            refetch,
            t,
            transmitSubmission,
            values,
        ]
    );

    if (establishmentsLoading || taxPayerLoading) {
        return <Loading />;
    }

    // Order the establishments to find the first submission date.
    const submissionsBySubmitBeforeDate = !establishmentsData
        ? []
        : [...establishmentsData?.fiskalyEstablishments?.data]?.sort((a, b) => {
              return a.submitBefore === b.submitBefore
                  ? 0
                  : a.submitBefore === null
                  ? 1
                  : b.submitBefore === null
                  ? -1
                  : a.submitBefore < b.submitBefore
                  ? 1
                  : -1;
          });

    const firstSubmissionDate =
        submissionsBySubmitBeforeDate?.length <= 0
            ? null
            : submissionsBySubmitBeforeDate[0].submitBefore;

    return (
        <View>
            <Headline size="h1">
                {t(
                    "backoffice.integration.fiskaly.establishment_submissions_header",
                    "Establishment Submissions"
                )}
            </Headline>
            {(integrationSettings?.data?.integrationSettings as FiskalySettings)
                ?.submissionNeeded && firstSubmissionDate ? (
                <View style={styles.container}>
                    <Alert type="warning">
                        {t(
                            "backoffice.integration.fiskaly.submission_needed_before",
                            "There is a need for a submission before {{date}}",
                            {
                                date: formatDate(
                                    firstSubmissionDate,
                                    locale as AvailableLocale
                                ),
                                interpolation: { escapeValue: false },
                            }
                        )}
                    </Alert>
                    <Spacer space={2} />
                </View>
            ) : null}

            {!taxPayerData?.getFiskalyTaxPayer ? (
                <View style={styles.container}>
                    <Alert type="warning">
                        {t(
                            "backoffice.integration.fiskaly.taxpayer_needed",
                            "A Tax Payer must be registered before submissions are sent. Please check the Tax Payer tab first."
                        )}
                    </Alert>
                    <Spacer space={2} />
                </View>
            ) : null}

            {establishmentsData?.fiskalyEstablishments.data.length === 0 ? (
                <View style={styles.container}>
                    <Alert type="info">
                        {t(
                            "backoffice.integration.fiskaly.submissions_no_establishments",
                            "No establishments found yet. Create some in the tab 'Establishments'."
                        )}
                    </Alert>
                    <Spacer space={2} />
                </View>
            ) : null}

            <SubmissionForm
                form={form}
                establishments={establishmentsData}
                taxpayer={taxPayerData}
                onSubmit={save}
                submitButton={["common.save", "Save"]}
            />
            <Spacer />
            <Divider />
            <Spacer />
            {error || establishmentsError || taxPayerError ? (
                <Alert type="error">
                    {t(
                        "backoffice.error.from_server",
                        "There was an error: {{errorText}}",
                        {
                            errorText:
                                error?.message ||
                                establishmentsError?.message ||
                                taxPayerError?.message,
                        }
                    )}
                </Alert>
            ) : null}
            {loading ? <Loading /> : null}

            <Table>
                <Table.Header>
                    <Table.Title>
                        {t(
                            "backoffice.integration.fiskaly.establishment",
                            "Establishment"
                        )}
                    </Table.Title>
                    <Table.Title style={styles.centerCell}>
                        {t("backoffice.integration.fiskaly.state", "State")}
                    </Table.Title>
                    <Table.Title style={styles.centerCell}>
                        {t("backoffice.integration.fiskaly.error", "Error")}
                    </Table.Title>
                    <Table.Title style={styles.centerCell}>
                        {t(
                            "backoffice.integration.fiskaly.submission_next_step",
                            "Next step"
                        )}
                    </Table.Title>
                    <Table.Title numeric>
                        {t(
                            "backoffice.integration.fiskaly.submission_requested_at",
                            "Requested at"
                        )}
                    </Table.Title>
                    <Table.Title numeric>
                        {t(
                            "backoffice.integration.fiskaly.submission_previewed_at",
                            "Previewed at"
                        )}
                    </Table.Title>
                    <Table.Title numeric>
                        {t(
                            "backoffice.integration.fiskaly.submission_reviewed_at",
                            "Reviewed at"
                        )}
                    </Table.Title>
                    <Table.Title numeric>
                        {t(
                            "backoffice.integration.fiskaly.submission_transmitted_at",
                            "Transmitted at"
                        )}
                    </Table.Title>
                </Table.Header>
                {data?.fiskalyEstablishmentSubmissions.data.length === 0 ? (
                    <Alert type="info">
                        {t(
                            "backoffice.integration.fiskaly.no_submissions",
                            "No submissions."
                        )}
                    </Alert>
                ) : null}

                {data?.fiskalyEstablishmentSubmissions.data.map(
                    (itr, rowIndex) => {
                        const foundEstablishment =
                            establishmentsData?.fiskalyEstablishments.data.find(
                                establItr =>
                                    itr.establishmentId === establItr.id
                            );

                        return (
                            <Table.Row
                                key={`row_${itr.id}`}
                                style={
                                    rowIndex % 2 === 0 ? styles.oddRow : null
                                }
                            >
                                <Table.Cell>
                                    {foundEstablishment
                                        ? foundEstablishment.designation
                                        : itr.establishmentId}
                                </Table.Cell>
                                <Table.Cell>{itr.state}</Table.Cell>
                                <Table.Cell>{itr.error}</Table.Cell>
                                <Table.Cell style={styles.centerCell}>
                                    {!itr.previewedAt ? (
                                        <Button
                                            size="tiny"
                                            style={styles.smallButton}
                                            onPress={() =>
                                                getPreviewFile(itr.id)
                                            }
                                        >
                                            {t(
                                                "backoffice.integration.fiskaly.submission_download_preview",
                                                "Download preview PDF"
                                            )}
                                        </Button>
                                    ) : null}
                                    {itr.previewedAt && !itr.reviewedAt ? (
                                        <Button
                                            size="tiny"
                                            style={styles.smallButton}
                                            onPress={() => {
                                                approveSubmission(itr.id);
                                            }}
                                        >
                                            {t(
                                                "backoffice.integration.fiskaly.submission_approve_ready",
                                                "Approve?"
                                            )}
                                        </Button>
                                    ) : null}
                                    {itr.reviewedAt && !itr.transmittedAt ? (
                                        <Button
                                            size="tiny"
                                            style={styles.smallButton}
                                            onPress={() => {
                                                startTransmission(itr.id);
                                            }}
                                        >
                                            {t(
                                                "backoffice.integration.fiskaly.submission_transmission_ready",
                                                "Ready to transmit?"
                                            )}
                                        </Button>
                                    ) : null}
                                    {itr.transmittedAt ? (
                                        <Button
                                            size="tiny"
                                            style={styles.smallButton}
                                            onPress={() => {
                                                getSubmissionFile(itr.id);
                                            }}
                                        >
                                            {t(
                                                "backoffice.integration.fiskaly.submission_download_pdf",
                                                "Download PDF"
                                            )}
                                        </Button>
                                    ) : null}
                                </Table.Cell>
                                <Table.Cell numeric>
                                    {formatRelativeDate(
                                        itr.createdAt,
                                        locale as AvailableLocale
                                    )}
                                </Table.Cell>
                                <Table.Cell numeric>
                                    {itr.previewedAt
                                        ? formatRelativeDate(
                                              itr.previewedAt,
                                              locale as AvailableLocale
                                          )
                                        : "-"}
                                </Table.Cell>
                                <Table.Cell numeric>
                                    {itr.reviewedAt
                                        ? formatRelativeDate(
                                              itr.reviewedAt,
                                              locale as AvailableLocale
                                          )
                                        : "-"}
                                </Table.Cell>
                                <Table.Cell numeric>
                                    {itr.transmittedAt
                                        ? formatRelativeDate(
                                              itr.transmittedAt,
                                              locale as AvailableLocale
                                          )
                                        : "-"}
                                </Table.Cell>
                            </Table.Row>
                        );
                    }
                )}

                <Table.Row>
                    <View style={styles.paginationHolder}>
                        <Button
                            size="tiny"
                            disabled={!!error}
                            onPress={() => {
                                if (!data?.fiskalyEstablishmentSubmissions) {
                                    return;
                                }

                                toast.info(
                                    t(
                                        "backoffice.common.cursor_pagination.first_page_message",
                                        "Fetching from the beginning..."
                                    )
                                );
                                refetch({
                                    pagination: {
                                        limit: data
                                            .fiskalyEstablishmentSubmissions
                                            ?.pagination.limit,
                                        nextPageToken: "", // We are not supplying a token, and therefore get the first results again.
                                    },
                                });
                            }}
                        >
                            {t(
                                "backoffice.common.cursor_pagination.first_page_label",
                                "Go to first page"
                            )}
                        </Button>
                        <Button
                            size="tiny"
                            disabled={
                                !!error ||
                                data?.fiskalyEstablishmentSubmissions.pagination
                                    .remaining === 0
                            }
                            onPress={() => {
                                if (!data?.fiskalyEstablishmentSubmissions) {
                                    return;
                                }

                                if (
                                    data.fiskalyEstablishmentSubmissions
                                        .pagination?.remaining > 0
                                )
                                    toast.info(
                                        t(
                                            "backoffice.common.cursor_pagination.next_page_message",
                                            "Fetching next page..."
                                        )
                                    );
                                refetch({
                                    pagination: {
                                        limit: data
                                            .fiskalyEstablishmentSubmissions
                                            ?.pagination.limit,
                                        nextPageToken:
                                            data.fiskalyEstablishmentSubmissions
                                                ?.pagination.token,
                                    },
                                });
                            }}
                        >
                            {t(
                                "backoffice.common.cursor_pagination.next_page_label",
                                "Next page"
                            )}
                        </Button>
                    </View>
                </Table.Row>
            </Table>
        </View>
    );
}

const styleFunc: StyleFunction = theme => ({
    container: {
        width: theme.dimensions.maxFormWidth,
    },
    centerCell: {
        justifyContent: "center",
    },

    smallButton: {
        paddingHorizontal: theme.spacingScale * 1.5,
    },
    oddRow: {
        ...theme.styles.oddRow,
    },
    textLink: {
        ...theme.styles.link,
    },

    disabled: {
        ...theme.styles.link,

        fontStyle: "italic",
        color: theme.colors.grey500,
        underlineColor: theme.colors.grey500,
        pointerEvents: "none",
    },

    paginationHolder: {
        ...theme.styles.flex1,
        ...theme.styles.row,
        padding: theme.spacingScale / 2,
        gap: theme.spacingScale,
    },
});
